import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Fade, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

/**
 * A reusable image viewer modal with zoom and navigation functionality
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Function to call when closing the modal
 * @param {array} images - Array of image objects with url, timestamp, track_id, age, gender properties
 * @param {number} initialIndex - Initial index of the image to display
 * @param {function} formatTimestamp - Function to format timestamp
 */
const ImageViewerModal = ({ open, onClose, images, initialIndex = 0, formatTimestamp }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset zoom and position when image changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  // Reset zoom and position when modal opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [open, initialIndex]);

  if (!images || images.length === 0) return null;

  // Make sure we have a valid currentImage
  const currentImage = images[currentIndex] || {};

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse events for dragging the image
  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      setPosition({
        x: position.x + deltaX,
        y: position.y + deltaY
      });

      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'Escape') {
      onClose();
    } else if (e.key === '+') {
      handleZoomIn();
    } else if (e.key === '-') {
      handleZoomOut();
    } else if (e.key === '0') {
      handleReset();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      onKeyDown={handleKeyDown}
      PaperProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          borderRadius: '8px',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ position: 'relative', height: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Top controls */}
        <Box
          sx={{
            p: 1.5,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Grid container spacing={1}>
            {/* Top row with timestamp and controls */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'medium', fontSize: '1.1rem' }}>
                {currentImage && currentImage.time_stamp ? (formatTimestamp ? formatTimestamp(currentImage.time_stamp) : currentImage.time_stamp) : 'NA'}
              </Typography>
              <Box>
                <IconButton onClick={handleZoomOut} sx={{ color: 'white' }}>
                  <ZoomOutIcon />
                </IconButton>
                <IconButton onClick={handleReset} sx={{ color: 'white' }}>
                  <RestartAltIcon />
                </IconButton>
                <IconButton onClick={handleZoomIn} sx={{ color: 'white' }}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Grid>
            
            {/* Details row with styled info badges */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)', 
                  borderRadius: '4px',
                  px: 1.5,
                  py: 0.5
                }}>
                  <Typography variant="body2" sx={{ color: '#a5d6ff', fontWeight: 'bold', mr: 0.5 }}>
                    Track ID:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {currentImage && currentImage.track_id ? currentImage.track_id : 'NA'}
                  </Typography>
                </Box>
                
                <Box className="hidden" sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)', 
                  borderRadius: '4px',
                  px: 1.5,
                  py: 0.5
                }}>
                  <Typography variant="body2" sx={{ color: '#a5d6ff', fontWeight: 'bold', mr: 0.5 }}>
                    Age:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {currentImage && currentImage.age ? currentImage.age : 'NA'}
                  </Typography>
                </Box>
                
                <Box className="hidden" sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)', 
                  borderRadius: '4px',
                  px: 1.5,
                  py: 0.5
                }}>
                  <Typography variant="body2" sx={{ color: '#a5d6ff', fontWeight: 'bold', mr: 0.5 }}>
                    Gender:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {currentImage && currentImage.gender ? currentImage.gender : 'NA'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Image container */}
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 0,
            position: 'relative',
            overflow: 'hidden',
            cursor: scale > 1 ? 'grab' : 'default'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Navigation buttons */}
          <Fade in={true}>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <NavigateBeforeIcon fontSize="large" />
            </IconButton>
          </Fade>

          <Fade in={true}>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <NavigateNextIcon fontSize="large" />
            </IconButton>
          </Fade>

          {/* Image */}
          <img
            src={currentImage && (currentImage.thumbnail || currentImage.url) ? (currentImage.thumbnail || currentImage.url) : 'https://via.placeholder.com/800x600?text=No+Image'}
            alt={`Evidence ${currentIndex + 1}`}
            style={{
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s',
              userSelect: 'none'
            }}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x600?text=Image+Error';
            }}
          />
        </DialogContent>

        {/* Image counter */}
        <Box
          sx={{
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Typography variant="body2" sx={{ color: 'white' }}>
            {currentIndex + 1} / {images.length}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImageViewerModal;
